import { createAsyncThunk } from "@reduxjs/toolkit";
import { getBestSellerById, getSaleData, getScreensaver } from "../services/customizerAPI";
import axiosClient from "../services/apiClient";
import AuthManager from "../../utils/AuthManager";
import { IAddFavoriteRequestPayload, IDeleteFavoriteRequestPayload } from "../types/builder";

export interface IGetScreensaverPayload {
  subpath?: string | null;
}

export interface IGetBestSellerProductPayload {
  id: string;
  data?: {
    size: string | number;
    engraving?: string;
    subpath: string;
    currencyCode?: 'USD' | 'CAD',
  }
}

export interface IAddBestSellerToCartPayload {
  protection_plan?: 1 | 0,
  forever_box?: 1 | 0;
  data: {
    size: number,
    bestseller_id: string,
    engraving: string,
    subpath: string,
    summaryContent: any,
    intergration_url: string,
    ring_url?: string,
    magic_number?: string,
    currencyCode?: 'USD' | 'CAD',
  }
}

export const getScreensaverData = createAsyncThunk(
  'GET_SCREENSAVER_DATA',
  async (payload?: IGetScreensaverPayload) => {
    const response = await getScreensaver(payload?.subpath ?? undefined);
    return response?.data;
  }
);

export const getBestSellerProduct = createAsyncThunk('lashbrook/getBestSellerProduct',
  async (payload: IGetBestSellerProductPayload, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await getBestSellerById(payload.id, payload.data);
      return fulfillWithValue(res.data)
    } catch (error) {
      return rejectWithValue(error);
    }
  });

const SALE_SUBPATH_KEY_MAP: Record<string, string> = {
  'revolution': 'custom_sale_percent',
  'foundry': 'custom_sale_percent_foundry',
  'origin-jewelry': 'custom_sale_percent_origin',
  'stonebrook-jewelry': 'custom_sale_percent_stonebrook',
};

export const getSaleDiscount = createAsyncThunk(
  'customizer/getSaleDiscount',
  async (subpath: string) => {
    const key = SALE_SUBPATH_KEY_MAP[subpath];
    if (!key) return null;
    const res = await getSaleData();
    const value = parseFloat(res.data[key]);
    return Number.isFinite(value) ? value * 100 : null;
  }
);

export const addBestSellerToCart = createAsyncThunk('lashbrook/addToCart',
  async ({ data }: IAddBestSellerToCartPayload, { fulfillWithValue, rejectWithValue }) => {
    try {
      const res = await axiosClient.post(`/api/v2/addBestSellerToCart`, data, {
        headers: {
          'subpath': data.subpath,
          'currency-code': data.currencyCode,
        }
      });
      return fulfillWithValue(res.data)
    } catch (error) {
      return rejectWithValue(error);
    }
  });

export const addFavourite = createAsyncThunk('lashbrook/addFavorite',
  async (data: IAddFavoriteRequestPayload, { fulfillWithValue, rejectWithValue }) => {
    try {
      const userId = AuthManager.getAnonymousUserId();
      data.user_id = userId;
      const res = await axiosClient.post('/api/v1/favorites', data);
      return fulfillWithValue(res.data);
    } catch (error) {
      return rejectWithValue(error);
    }
  });

export const getFavourites = createAsyncThunk('lashbrook/getFavorites',
  async (_, { fulfillWithValue, rejectWithValue }) => {
    try {
      const userId = AuthManager.getAnonymousUserId();
      const res = await axiosClient.get(`/api/v1/favorites${userId ? `?user_id=${userId}` : ''}`);
      return fulfillWithValue(res.data);
    } catch (error) {
      return rejectWithValue(error);
    }
  });

export const deleteFavourite = createAsyncThunk('lashbrook/deleteFavorite',
  async (data: IDeleteFavoriteRequestPayload, { fulfillWithValue, rejectWithValue }) => {
    try {
      const userId = AuthManager.getAnonymousUserId();
      const res = await axiosClient.delete(`/api/v1/favorites${userId ? `?user_id=${userId}` : ''}`, { data });
      return fulfillWithValue(res.data);
    } catch (error) {
      return rejectWithValue(error);
    }
  });